import type { SectionCoverageRow } from '../services/dataforseo';
import {
  buildMetaPassages,
  buildPassagesFromClosestFocalSections,
  buildPassagesFromMarkdown,
  buildPassagesFromSectionRows,
  computeKeywordPassageEvidence,
  passageBodyForDisplay,
  type KeywordPassageMatch,
  type PassageKind,
  type PassageSource
} from './keyword-passage-evidence';

export const MIN_SEARCH_VOLUME = 50;
export const MAX_RIVAL_POSITION = 15;
export const MIN_POSITION_DELTA = 5;
export const MAX_GAPS_RETURNED = 8;

export interface RankedKeywordEntry {
  keyword: string;
  position: number;
  searchVolume?: number;
}

export interface RankDrivenSectionGap {
  keyword: string;
  /**
   * Other keywords the rival ranks for that point at THIS SAME section.
   *
   * One section wanted by three queries is one thing to write with three
   * reasons, not three findings — and until these were merged, the extra
   * keywords were silently dropped onto whatever section was still free.
   */
  groupedKeywords?: string[];
  /** Combined monthly volume of `keyword` plus `groupedKeywords`. */
  groupedSearchVolume?: number;
  /**
   * Word-order variants of this keyword that were folded into this row.
   *
   * Google reports one aggregated volume for close variants, so a rival
   * ranking for "delivery service model", "models of service delivery" and
   * "services delivery model" is ranking for ONE query reported three ways.
   * Kept rather than discarded: the operator should be able to see what was
   * merged, just not be asked to act on it three times.
   */
  variantKeywords?: string[];
  rivalPosition: number;
  focalPosition: number | null;
  searchVolume: number;
  positionDelta: number;
  sectionHeading: string;
  sectionHeadingPath: string;
  sectionNumber?: number;
  matchStrength: 'heading' | 'body' | 'both';
  priorityScore: number;
  evidenceLine: string;
  sectionCovered: boolean;
  closestMyHeading: string | null;
  closestMyHeadingPath: string | null;
  sectionSimilarity: number;
  absentTerms: string[];
  focalCoverageLine: string;
  rivalPassage: string | null;
  rivalPassageHeading: string | null;
  rivalPassageHeadingLevel?: number | null;
  rivalPassageKind?: PassageKind | null;
  focalPassage: string | null;
  focalPassageHeading: string | null;
  focalPassageHeadingLevel?: number | null;
  focalPassageKind?: PassageKind | null;
  queryFamilyMismatch: boolean;
  mismatchNote: string | null;
}

export interface ComputeRankDrivenSectionGapsInput {
  focalQuery?: string | null;
  focalKeywords?: RankedKeywordEntry[] | null;
  rivalKeywords?: RankedKeywordEntry[] | null;
  sectionRows?: SectionCoverageRow[] | null;
  focalAnalysis?: any;
  rivalAnalysis?: any;
}

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'for', 'to', 'of', 'in', 'on', 'at', 'by',
  'is', 'are', 'was', 'were', 'be', 'with', 'from', 'as', 'it', 'its', 'your',
  'you', 'what', 'how', 'why', 'when', 'where', 'who', 'vs', 'best', 'top'
]);

function normalizeKeyword(keyword: string): string {
  return keyword.toLowerCase().trim().replace(/\s+/g, ' ');
}

function meaningfulTokens(text: string): string[] {
  return normalizeKeyword(text)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3 && !STOP_WORDS.has(t));
}

function sharesQueryRelevance(keyword: string, focalQuery: string): boolean {
  const query = normalizeKeyword(focalQuery);
  const kw = normalizeKeyword(keyword);
  if (!query || !kw) return true;
  if (kw === query) return true;
  if (kw.includes(query) || query.includes(kw)) return true;

  const queryTokens = meaningfulTokens(query);
  const keywordTokens = meaningfulTokens(kw);
  if (!queryTokens.length || !keywordTokens.length) return true;

  const querySet = new Set(queryTokens);
  const sharedCount = keywordTokens.filter((t) => querySet.has(t)).length;
  return sharedCount >= 2;
}

/**
 * Same query as the page's own target, allowing for word order and plurals.
 *
 * Google aggregates volume across close variants, so "delivery service model",
 * "models of service delivery" and "services delivery model" are one query
 * reported three ways — all three carried 260/mo and the same positions on the
 * pilot. Compared on a sorted, singularised token set so none of the three
 * counts as separate from the page's target.
 *
 * A keyword with no meaningful tokens is never a restatement: there is nothing
 * to compare, and dropping it would be a guess in the direction that hides work.
 */
function isFocalQueryRestatement(keyword: string, focalQuery: string): boolean {
  const focal = keywordSignature(focalQuery);
  if (!focal) return false;
  const kw = keywordSignature(keyword);
  if (!kw) return false;
  return kw === focal;
}

/** Sorted, singularised meaningful tokens — order- and plural-insensitive. */
function keywordSignature(text: string): string {
  return meaningfulTokens(text)
    .map((t) => (t.length > 3 && t.endsWith('s') && !t.endsWith('ss') ? t.slice(0, -1) : t))
    .sort()
    .join('|');
}

function positionWeight(position: number): number {
  if (position <= 3) return 3;
  if (position <= 10) return 2;
  return 1;
}

function buildKeywordMap(
  keywords: RankedKeywordEntry[] | null | undefined
): Map<string, RankedKeywordEntry> {
  const map = new Map<string, RankedKeywordEntry>();
  for (const row of keywords || []) {
    const normalized = normalizeKeyword(row.keyword || '');
    if (!normalized) continue;
    const existing = map.get(normalized);
    if (!existing || (row.position || 999) < (existing.position || 999)) {
      map.set(normalized, row);
    }
  }
  return map;
}

interface KeywordCandidate {
  keyword: string;
  rivalPosition: number;
  focalPosition: number | null;
  searchVolume: number;
  positionDelta: number;
  kind: 'shared' | 'rival_unique';
}

function buildKeywordCandidates(
  focalKeywords: RankedKeywordEntry[] | null | undefined,
  rivalKeywords: RankedKeywordEntry[] | null | undefined,
  focalQuery: string
): KeywordCandidate[] {
  const focalMap = buildKeywordMap(focalKeywords);
  const rivalMap = buildKeywordMap(rivalKeywords);
  const candidates: KeywordCandidate[] = [];
  const seen = new Set<string>();

  rivalMap.forEach((rivalKw, normalized) => {
    const rivalPosition = rivalKw.position || 999;
    if (rivalPosition > MAX_RIVAL_POSITION) return;

    const searchVolume = Number(rivalKw.searchVolume) || 0;
    const focalKw = focalMap.get(normalized);

    if (focalKw) {
      const focalPos = focalKw.position || 999;
      const positionDelta = focalPos - rivalPosition;
      if (positionDelta < MIN_POSITION_DELTA) return;
      const key = `shared:${normalized}`;
      if (seen.has(key)) return;
      seen.add(key);
      candidates.push({
        keyword: rivalKw.keyword,
        rivalPosition,
        focalPosition: focalPos,
        searchVolume: searchVolume || Number(focalKw.searchVolume) || 0,
        positionDelta,
        kind: 'shared'
      });
      return;
    }

    if (searchVolume < MIN_SEARCH_VOLUME) return;
    const key = `unique:${normalized}`;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push({
      keyword: rivalKw.keyword,
      rivalPosition,
      focalPosition: null,
      searchVolume,
      positionDelta: MAX_RIVAL_POSITION,
      kind: 'rival_unique'
    });
  });

  if (!focalQuery.trim()) return candidates;
  return candidates
    .filter((c) => sharesQueryRelevance(c.keyword, focalQuery))
    // The page's own target keyword is not a finding.
    //
    // This card exists to surface a SECONDARY query the rival ranks for and
    // then name the section that earns it — "they rank for 'yeast making' and
    // here is their 'make your own yeast' section". Restating the query the
    // page is already measured on says only "they beat you", which the header
    // above already says, and it dominated the card: on the pilot, 138 of 142
    // rows were re-wordings of the page's own keyword.
    .filter((c) => !isFocalQueryRestatement(c.keyword, focalQuery));
}

interface SectionMatch {
  row: SectionCoverageRow;
  matchStrength: 'heading' | 'body' | 'both';
  matchScore: number;
}

function scoreSectionMatch(keyword: string, row: SectionCoverageRow): SectionMatch | null {
  if (row.isNonTopical) return null;

  const kw = normalizeKeyword(keyword);
  const heading = normalizeKeyword(row.competitorHeading || '');
  const body = normalizeKeyword(row.competitorText || '');

  const headingMatch = heading.includes(kw) || kw.split(' ').every((t) => heading.includes(t));
  const bodyMatch = body.includes(kw);

  if (!headingMatch && !bodyMatch) return null;

  let matchStrength: SectionMatch['matchStrength'] = 'body';
  let matchScore = 1;
  if (headingMatch && bodyMatch) {
    matchStrength = 'both';
    matchScore = 2.5;
  } else if (headingMatch) {
    matchStrength = 'heading';
    matchScore = 2;
  }

  if (!row.sectionCovered) matchScore += 1;
  return { row, matchStrength, matchScore };
}

function bestSectionForKeyword(
  keyword: string,
  sectionRows: SectionCoverageRow[]
): SectionMatch | null {
  let best: SectionMatch | null = null;
  for (const row of sectionRows) {
    const match = scoreSectionMatch(keyword, row);
    if (!match) continue;
    if (!best || match.matchScore > best.matchScore) best = match;
  }
  return best;
}

function formatVolume(volume: number): string {
  if (!Number.isFinite(volume) || volume <= 0) return '—';
  return volume.toLocaleString('en-US');
}

const STRONG_FOCAL_SIMILARITY = 0.55;

function formatAbsentTermsPreview(absentTerms: string[]): string {
  const terms = absentTerms.filter(Boolean);
  if (!terms.length) return '';
  const preview = terms.slice(0, 4).join(', ');
  return terms.length > 4 ? `${preview} (+${terms.length - 4} more)` : preview;
}

/**
 * ISS001-7 / findings §16 — mirror of `backend/src/services/rank-driven-section-gaps.ts`.
 * Keep the copy identical in both: `scripts/issues-retest.ts` asserts the backend
 * copy, and the drawer renders this one.
 */
export type SectionGapOutlook = 'sections-missing' | 'outranked-on-covered' | 'no-gaps';

export interface SectionGapVerdict {
  outlook: SectionGapOutlook;
  headline: string;
  nextStep: string;
}

export function buildSectionGapVerdict(input: {
  missingSectionCount: number | null | undefined;
  rankDrivenGapCount: number | null | undefined;
}): SectionGapVerdict {
  const missing = Math.max(0, Math.trunc(Number(input.missingSectionCount)) || 0);
  const rankDriven = Math.max(0, Math.trunc(Number(input.rankDrivenGapCount)) || 0);

  if (missing > 0) {
    return { outlook: 'sections-missing', headline: '', nextStep: '' };
  }

  if (rankDriven > 0) {
    const queries = rankDriven === 1 ? 'query' : 'queries';
    return {
      outlook: 'outranked-on-covered',
      headline: `No missing sections — you cover the same ground. They still outrank you on ${rankDriven} ${queries} your page already covers.`,
      nextStep:
        'Adding sections will not close this. Work the passages below — deepen the thin ones, or retitle so the query is unmistakable.'
    };
  }

  return {
    outlook: 'no-gaps',
    headline:
      'Nothing to take from this page — no section yours is missing, and no keyword where it clearly outranks you.',
    nextStep:
      'Content is not the lever against this rival. Compare authority, internal links or freshness instead — or run this against a different page from the SERP.'
  };
}

export function buildFocalCoverageLine(row: SectionCoverageRow): string {
  const closest = (row.closestMyHeadingPath || row.closestMyHeading || '').trim();
  const pct = Math.round((row.sectionSimilarity || 0) * 100);
  const absentPreview = formatAbsentTermsPreview(row.absentTerms || []);
  const rivalHeading = (row.competitorHeading || '').trim();

  if (!row.sectionCovered) {
    return rivalHeading
      ? `No equivalent section on your page — add a block like "${rivalHeading}".`
      : 'No equivalent section on your page — add a block like theirs.';
  }

  if (absentPreview) {
    if (closest) {
      return `Shallow coverage — closest: "${closest}" (${pct}%) — missing: ${absentPreview}.`;
    }
    return `Topic mentioned but missing phrases: ${absentPreview}.`;
  }

  if (closest && (row.sectionSimilarity || 0) >= STRONG_FOCAL_SIMILARITY) {
    return `Topic present under "${closest}" (${pct}%) — they still outrank you; deepen or retitle.`;
  }

  if (closest) {
    return `Partially covered — closest: "${closest}" (${pct}%).`;
  }

  return 'Topic addressed somewhere on your page — they still outrank you for this keyword.';
}

function findSectionRowByNumber(
  sectionRows: SectionCoverageRow[],
  sectionNumber?: number
): SectionCoverageRow | null {
  if (sectionNumber == null) return null;
  return sectionRows.find((r) => r.sectionNumber === sectionNumber) ?? null;
}

const EMPTY_PASSAGE_FIELDS = {
  rivalPassage: null as string | null,
  rivalPassageHeading: null as string | null,
  rivalPassageHeadingLevel: null as number | null,
  rivalPassageKind: null as PassageKind | null,
  focalPassage: null as string | null,
  focalPassageHeading: null as string | null,
  focalPassageHeadingLevel: null as number | null,
  focalPassageKind: null as PassageKind | null,
  queryFamilyMismatch: false,
  mismatchNote: null as string | null
};

function passageFieldsFromMatch(match: KeywordPassageMatch | null | undefined) {
  if (!match) {
    return {
      passage: null as string | null,
      passageHeading: null as string | null,
      passageHeadingLevel: null as number | null,
      passageKind: null as PassageKind | null
    };
  }
  const body = passageBodyForDisplay(match.passageHeading, match.passage, match.kind);
  return {
    passage: body || null,
    passageHeading: match.passageHeading || null,
    passageHeadingLevel: match.passageHeadingLevel ?? null,
    passageKind: match.kind
  };
}

function attachFocalComparison(
  gap: Omit<
    RankDrivenSectionGap,
    | 'sectionCovered'
    | 'closestMyHeading'
    | 'closestMyHeadingPath'
    | 'sectionSimilarity'
    | 'absentTerms'
    | 'focalCoverageLine'
    | 'rivalPassage'
    | 'rivalPassageHeading'
    | 'focalPassage'
    | 'focalPassageHeading'
    | 'queryFamilyMismatch'
    | 'mismatchNote'
  >,
  sectionRow: SectionCoverageRow | null
): RankDrivenSectionGap {
  if (!sectionRow) {
    return {
      ...gap,
      ...EMPTY_PASSAGE_FIELDS,
      sectionCovered: false,
      closestMyHeading: null,
      closestMyHeadingPath: null,
      sectionSimilarity: 0,
      absentTerms: [],
      focalCoverageLine: 'Section comparison unavailable.'
    };
  }

  return {
    ...gap,
    ...EMPTY_PASSAGE_FIELDS,
    sectionCovered: sectionRow.sectionCovered,
    closestMyHeading: sectionRow.closestMyHeading ?? null,
    closestMyHeadingPath: sectionRow.closestMyHeadingPath ?? null,
    sectionSimilarity: sectionRow.sectionSimilarity || 0,
    absentTerms: sectionRow.absentTerms || [],
    focalCoverageLine: buildFocalCoverageLine(sectionRow)
  };
}

export function enrichRankDrivenGapsFocalComparison(
  gaps: RankDrivenSectionGap[],
  sectionRows: SectionCoverageRow[] | null | undefined
): RankDrivenSectionGap[] {
  const rows = sectionRows || [];
  return gaps.map((gap) => {
    if (gap.focalCoverageLine) return gap;
    const sectionRow = findSectionRowByNumber(rows, gap.sectionNumber);
    return attachFocalComparison(gap, sectionRow);
  });
}

function gapNeedsFocalEnrichment(gaps: RankDrivenSectionGap[]): boolean {
  return gaps.some((g) => !g.focalCoverageLine);
}

function buildEvidenceLine(candidate: KeywordCandidate, section: SectionMatch): string {
  const vol = formatVolume(candidate.searchVolume);
  const focalPart =
    candidate.focalPosition != null
      ? `you rank #${candidate.focalPosition}`
      : 'you do not rank';
  return `They rank #${candidate.rivalPosition} for "${candidate.keyword}" (${vol}/mo) — ${focalPart}.`;
}

function extractMetaFromAnalysis(analysis: any): {
  title: string;
  description: string;
  h1: string;
} {
  const meta = analysis?.rawData?.pageData?.meta;
  const title =
    String(meta?.title || analysis?.rawData?.summary?.title || '').trim();
  const h1Arr = meta?.htags?.h1;
  const h1 = Array.isArray(h1Arr) && typeof h1Arr[0] === 'string' ? h1Arr[0].trim() : '';
  return {
    title,
    description: String(meta?.description || '').trim(),
    h1
  };
}

function structuredContentToMarkdown(structuredContent: any): string {
  if (!structuredContent?.main_topic || !Array.isArray(structuredContent.main_topic)) {
    return '';
  }
  const parts: string[] = [];
  for (const topic of structuredContent.main_topic) {
    const title = String(topic?.h_title || '').trim();
    const level = Number(topic?.level) || 2;
    const hashes = '#'.repeat(Math.min(6, Math.max(1, level)));
    if (title) parts.push(`${hashes} ${title}`);
    const blocks = [
      ...(Array.isArray(topic?.primary_content) ? topic.primary_content : []),
      ...(Array.isArray(topic?.secondary_content) ? topic.secondary_content : [])
    ];
    for (const block of blocks) {
      const text = String(block?.text || '').trim();
      if (text) parts.push(text);
    }
  }
  return parts.join('\n\n');
}

function analysisBodyMarkdown(analysis: any): string {
  const raw = analysis?.rawData;
  if (!raw) return '';
  const md = raw.pageAsMarkdown || raw.page_as_markdown;
  if (typeof md === 'string' && md.trim()) return md.trim();
  const structured = structuredContentToMarkdown(raw.structuredContent);
  if (structured.length >= 80) return structured;
  const extracted = raw.extractedText;
  if (typeof extracted === 'string' && extracted.trim()) {
    const meta = extractMetaFromAnalysis(analysis);
    const heading = meta.h1 || meta.title;
    if (heading) return `# ${heading}\n\n${extracted.trim()}`;
    return extracted.trim();
  }
  if (structured.length > 0) return structured;
  const meta = extractMetaFromAnalysis(analysis);
  if (meta.h1) return `# ${meta.h1}`;
  if (meta.title) return `# ${meta.title}`;
  return '';
}

function buildPassageSources(
  sectionRows: SectionCoverageRow[],
  analysis: any,
  options?: { closestFocalFromRows?: SectionCoverageRow[] | null }
): PassageSource[] {
  const meta = extractMetaFromAnalysis(analysis);
  const body = analysisBodyMarkdown(analysis);
  const fromSections = sectionRows.length
    ? buildPassagesFromSectionRows(sectionRows)
    : buildPassagesFromMarkdown(body);
  const lexical = buildMetaPassages(meta.title, meta.description, meta.h1);
  const closestFocal = options?.closestFocalFromRows?.length
    ? buildPassagesFromClosestFocalSections(options.closestFocalFromRows)
    : [];
  return [...lexical, ...fromSections, ...closestFocal];
}

function gapNeedsPassageEnrichment(gaps: RankDrivenSectionGap[]): boolean {
  return gaps.some((g) => !g.rivalPassage);
}

export function enrichRankDrivenGapsWithPassageEvidence(
  gaps: RankDrivenSectionGap[],
  ctx: {
    focalQuery?: string | null;
    sectionRows?: SectionCoverageRow[] | null;
    focalAnalysis?: any;
    rivalAnalysis?: any;
  }
): RankDrivenSectionGap[] {
  if (!gaps.length) return gaps;

  const sectionRows = (ctx.sectionRows || []).filter((r) => !r.isNonTopical);
  const rivalPassages = buildPassageSources(sectionRows, ctx.rivalAnalysis);
  const focalPassages = buildPassageSources([], ctx.focalAnalysis, {
    closestFocalFromRows: sectionRows
  });
  if (!rivalPassages.length) return gaps;

  const evidence = computeKeywordPassageEvidence({
    keywords: gaps.map((g) => ({
      keyword: g.keyword,
      rivalPosition: g.rivalPosition,
      focalPosition: g.focalPosition,
      searchVolume: g.searchVolume
    })),
    focalQuery: ctx.focalQuery,
    rivalPassages,
    focalPassages
  });

  const byKeyword = new Map(evidence.map((e) => [e.keyword.toLowerCase(), e]));

  return gaps.map((gap) => {
    const row = byKeyword.get(gap.keyword.toLowerCase());
    if (!row) return { ...gap, ...EMPTY_PASSAGE_FIELDS };
    const rival = passageFieldsFromMatch(row.rival);
    const focal = passageFieldsFromMatch(row.focal);
    return {
      ...gap,
      rivalPassage: rival.passage,
      rivalPassageHeading: rival.passageHeading,
      rivalPassageHeadingLevel: rival.passageHeadingLevel,
      rivalPassageKind: rival.passageKind,
      focalPassage: focal.passage,
      focalPassageHeading: focal.passageHeading,
      focalPassageHeadingLevel: focal.passageHeadingLevel,
      focalPassageKind: focal.passageKind,
      queryFamilyMismatch: row.queryFamilyMismatch,
      mismatchNote: row.mismatchNote
    };
  });
}

function computePriorityScore(
  candidate: KeywordCandidate,
  section: SectionMatch,
  focalQuery: string
): number {
  const volume = Math.max(candidate.searchVolume, 1);
  const queryBoost = sharesQueryRelevance(candidate.keyword, focalQuery) ? 1.5 : 1;
  const deltaBoost =
    candidate.kind === 'shared'
      ? Math.min(3, Math.max(1, candidate.positionDelta / 5))
      : 1;

  return (
    volume *
    positionWeight(candidate.rivalPosition) *
    section.matchScore *
    queryBoost *
    deltaBoost
  );
}

export function computeRankDrivenSectionGaps(
  input: ComputeRankDrivenSectionGapsInput
): RankDrivenSectionGap[] {
  const focalQuery = (input.focalQuery || '').trim();
  const sectionRows = (input.sectionRows || []).filter((r) => !r.isNonTopical);
  if (!sectionRows.length) return [];
  if (!(input.rivalKeywords || []).length) return [];

  const candidates = buildKeywordCandidates(
    input.focalKeywords,
    input.rivalKeywords,
    focalQuery
  );
  if (!candidates.length) return [];

  const gaps: RankDrivenSectionGap[] = [];
  // Section number -> the row that claimed it, so a second keyword wanting the
  // same section JOINS it instead of being pushed onto its second choice.
  const bySection = new Map<number, RankDrivenSectionGap>();

  const sortedCandidates = [...candidates].sort((a, b) => {
    const volDiff = (b.searchVolume || 0) - (a.searchVolume || 0);
    if (volDiff !== 0) return volDiff;
    return (a.rivalPosition || 999) - (b.rivalPosition || 999);
  });

  for (const candidate of sortedCandidates) {
    const sectionMatch = bestSectionForKeyword(candidate.keyword, sectionRows);
    if (!sectionMatch) continue;

    const sectionNum = sectionMatch.row.sectionNumber;
    // This is where the "sections look random" came from.
    //
    // The rule used to be one gap per section, enforced by SKIPPING any later
    // keyword whose best section was already taken. So on a page where three
    // near-identical keywords all wanted "What is a service delivery model?",
    // the first took it and the other two were pushed down to their second and
    // third choices — three keywords, three unrelated-looking sections, none of
    // them the section the keyword actually matched.
    //
    // A section wanted by three keywords is not three findings, and it is not a
    // reason to bury two of them. It is one section to write, with three
    // reasons to write it.
    const claimed = sectionNum != null ? bySection.get(sectionNum) : undefined;
    if (claimed) {
      const already = new Set([
        keywordSignature(claimed.keyword),
        ...(claimed.groupedKeywords || []).map(keywordSignature)
      ]);
      if (!already.has(keywordSignature(candidate.keyword))) {
        claimed.groupedKeywords = [...(claimed.groupedKeywords || []), candidate.keyword];
        claimed.groupedSearchVolume =
          (claimed.groupedSearchVolume || claimed.searchVolume || 0) +
          (candidate.searchVolume || 0);
      }
      continue;
    }

    gaps.push(
      attachFocalComparison(
        {
          keyword: candidate.keyword,
          rivalPosition: candidate.rivalPosition,
          focalPosition: candidate.focalPosition,
          searchVolume: candidate.searchVolume,
          positionDelta: candidate.positionDelta,
          sectionHeading: sectionMatch.row.competitorHeading,
          sectionHeadingPath:
            sectionMatch.row.competitorHeadingPath || sectionMatch.row.competitorHeading,
          sectionNumber: sectionMatch.row.sectionNumber,
          matchStrength: sectionMatch.matchStrength,
          priorityScore: computePriorityScore(candidate, sectionMatch, focalQuery),
          evidenceLine: buildEvidenceLine(candidate, sectionMatch)
        },
        sectionMatch.row
      )
    );

    if (sectionNum != null) bySection.set(sectionNum, gaps[gaps.length - 1]);
  }

  const sorted = gaps
    .sort((a, b) => b.priorityScore - a.priorityScore)
    .slice(0, MAX_GAPS_RETURNED);

  return enrichRankDrivenGapsWithPassageEvidence(sorted, {
    focalQuery,
    sectionRows,
    focalAnalysis: input.focalAnalysis,
    rivalAnalysis: input.rivalAnalysis
  });
}

export function extractRankedKeywordsFromAnalysis(analysis: any): RankedKeywordEntry[] {
  const keywords = analysis?.rankedKeywords?.keywords || [];
  return keywords
    .map((k: any) => ({
      keyword: String(k.keyword || '').trim(),
      position: Number(k.position) || 999,
      searchVolume: Number(k.searchVolume) || 0
    }))
    .filter((k: RankedKeywordEntry) => k.keyword.length > 0);
}

/**
 * One row per query, not one per spelling of it.
 *
 * On the pilot, /blog/service-delivery-models showed three findings —
 * "delivery service model", "models of service delivery", "services delivery
 * model" — each paired with a different section of argano.com's page. They are
 * the same query: identical search volume (260), identical rival position (#1)
 * and identical focal position (#21). Google aggregates volume across close
 * variants, so three rows reporting 260/mo is one opportunity counted three
 * times, and the three different sections read as an arbitrary pick because
 * nothing distinguishes the keywords that chose them.
 *
 * The signature is the keyword's meaningful tokens, singularised and sorted, so
 * word order and plurals stop mattering. Stop words are already excluded by
 * `meaningfulTokens`, which is what lets "models OF service delivery" match.
 *
 * The survivor is the highest-priority row, then the strongest section match —
 * so the section shown is the best-evidenced one rather than whichever variant
 * happened to sort first. The others are kept on `variantKeywords`, because an
 * operator who wonders where the other spellings went deserves an answer.
 */
export function dedupeRankGapVariants(
  gaps: readonly RankDrivenSectionGap[] | null | undefined
): RankDrivenSectionGap[] {
  const rows = (gaps || []).filter(Boolean);
  if (rows.length < 2) return [...rows];

  const signature = (keyword: string): string =>
    meaningfulTokens(keyword)
      .map((t) => (t.length > 3 && t.endsWith('s') && !t.endsWith('ss') ? t.slice(0, -1) : t))
      .sort()
      .join('|');

  // A keyword with no meaningful tokens cannot be compared, so it keeps its own
  // bucket rather than joining every other uncomparable row.
  const buckets = new Map<string, RankDrivenSectionGap[]>();
  rows.forEach((gap, index) => {
    const key = signature(gap.keyword || '') || `__unkeyed_${index}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(gap);
    else buckets.set(key, [gap]);
  });

  const out: RankDrivenSectionGap[] = [];
  for (const bucket of buckets.values()) {
    if (bucket.length === 1) {
      out.push(bucket[0]);
      continue;
    }
    const ranked = [...bucket].sort(
      (a, b) =>
        (b.priorityScore || 0) - (a.priorityScore || 0) ||
        (b.sectionSimilarity || 0) - (a.sectionSimilarity || 0) ||
        (a.keyword || '').length - (b.keyword || '').length
    );
    const [best, ...rest] = ranked;
    out.push({
      ...best,
      variantKeywords: rest.map((g) => (g.keyword || '').trim()).filter(Boolean)
    });
  }

  // Original order, so a dedupe never silently re-ranks the card.
  const orderOf = new Map(rows.map((g, i) => [g, i]));
  return out.sort((a, b) => (orderOf.get(a) ?? 0) - (orderOf.get(b) ?? 0));
}

export function rankDrivenGapsForV2Row(
  row: {
    rankDrivenGaps?: RankDrivenSectionGap[];
    sectionCoverageGroups?: SectionCoverageRow[];
    sectionCoverage?: SectionCoverageRow[];
  } | null | undefined,
  options?: {
    focalQuery?: string;
    focalAnalysis?: any;
    rivalAnalysis?: any;
  }
): RankDrivenSectionGap[] {
  const sectionRows = row?.sectionCoverageGroups?.length
    ? row.sectionCoverageGroups
    : row?.sectionCoverage || [];

  let gaps: RankDrivenSectionGap[];

  if (row?.rankDrivenGaps?.length) {
    gaps = row.rankDrivenGaps;
    if (gapNeedsFocalEnrichment(gaps)) {
      gaps = enrichRankDrivenGapsFocalComparison(gaps, sectionRows);
    }
  } else if (!row || !options) {
    return [];
  } else {
    gaps = computeRankDrivenSectionGaps({
      focalQuery: options.focalQuery,
      focalKeywords: extractRankedKeywordsFromAnalysis(options.focalAnalysis),
      rivalKeywords: extractRankedKeywordsFromAnalysis(options.rivalAnalysis),
      sectionRows,
      focalAnalysis: options.focalAnalysis,
      rivalAnalysis: options.rivalAnalysis
    });
  }

  // Re-enrich whenever we have section rows and/or crawl analyses — focal passages
  // can also come from closestMy* on section coverage without full body text.
  if (gaps.length && (sectionRows.length || options?.focalAnalysis || options?.rivalAnalysis)) {
    gaps = enrichRankDrivenGapsWithPassageEvidence(gaps, {
      focalQuery: options?.focalQuery,
      sectionRows,
      focalAnalysis: options?.focalAnalysis,
      rivalAnalysis: options?.rivalAnalysis
    });
  }

  // Applied to STORED rows too, not only freshly computed ones.
  //
  // A snapshot written before this rule still carries rows for the page's own
  // keyword, and the pilot is mid-review with no re-crawl planned. Filtering
  // here means the belt stops showing them today; the compute-side rule keeps
  // them out of everything written from now on.
  const secondary = (options?.focalQuery || '').trim()
    ? gaps.filter((g) => !isFocalQueryRestatement(g.keyword || '', options!.focalQuery!))
    : gaps;

  // Last, so it folds whichever path produced the rows — stored or computed —
  // and after enrichment, when `sectionSimilarity` is populated and can break
  // the tie between two equally-scored variants.
  return dedupeRankGapVariants(secondary);
}
